import logger from "../../utils/logger";

// functions/warehouse_page/warehouse_page_functions.jsx

// Import API routes
import { 
    deletePallet,
    addProductToPallet,
    updatePalletItemQuantity,
    removePalletItem,
    movePalletItem
} from "../../api/enhancedWarehouse";

export class PalletEditorFunctions {

    constructor(showAlert) {
        this.showAlert = showAlert;
    }

    async deletePallet(palletId, onSave, pallet, setLoading) {
        if (!palletId) {
            this.showAlert({
                message: "No pallet to delete",
                type: "warning",
                buttons: [{ label: "Close", type: "primary" }],
            });
            return;
        }

        // Show confirmation dialog
        this.showAlert({
            message: `Are you sure you want to delete pallet? This action cannot be undone and will remove all items from this pallet.`,
            type: "danger",
            buttons: [
                { 
                    label: "Cancel", 
                    type: "secondary",
                    onClick: () => {} // Do nothing on cancel
                },
                { 
                    label: "Delete Pallet", 
                    type: "danger",
                    onClick: async () => {
                        setLoading(true);
                        try {
                            const result = await deletePallet(pallet.id);

                            if (result.success) {
                                this.showAlert({
                                    message: result.message || 'Pallet deleted successfully',
                                    type: 'success',
                                    buttons: [{ label: 'Close', type: "primary" }]
                                });

                                // Close the editor and trigger refresh
                                onSave();
                            } else {
                                throw new Error(result.message || 'Failed to delete pallet');
                            }
                        } catch (error) {
                            logger.error('Error deleting pallet:', error);
                            this.showAlert({
                                message: `Failed to delete pallet: ${error.message}`,
                                type: 'danger',
                                buttons: [{ label: 'Close', type: "primary" }]
                            });
                        } finally {
                            setLoading(false);
                        }
                    }
                }
            ]
        });
    }

    async addProductToPallet(pallet, selectedProduct, productQuantity, productNotes, availableProducts, setPalletItems, setSelectedProduct, setProductQuantity, setProductNotes, palletItems = []) {
        if (!selectedProduct || !productQuantity || !pallet) {
            this.showAlert({
                message: "Please select a product and enter quantity",
                type: "warning",
                buttons: [{ label: "Close", type: "primary" }],
            });
            return { success: false };
        }

        try {
            // Check if product already exists in the pallet
            const existingItem = palletItems.find(item => item.product_id === selectedProduct);
            
            if (existingItem) {
                // Product already exists, update quantity instead
                const newQuantity = existingItem.quantity + parseInt(productQuantity);
                const combinedNotes = [existingItem.notes, productNotes.trim()].filter(Boolean).join('; ') || null;
                
                // Get the product details to ensure we have complete data
                const product = availableProducts.find(p => p.id === selectedProduct);
                
                const response = await updatePalletItemQuantity(pallet.id, existingItem.id, newQuantity, combinedNotes);

                if (response.success || response.status === 200) {
                    // Update local state immediately - preserve all existing item data and ensure product is complete
                    setPalletItems(prevItems => 
                        prevItems.map(item => 
                            item.id === existingItem.id 
                                ? { 
                                    ...item, 
                                    quantity: newQuantity, 
                                    notes: combinedNotes,
                                    // Ensure product data is complete
                                    product: product || item.product || { 
                                        id: selectedProduct, 
                                        product_code: 'Unknown', 
                                        product_name: 'Product not found',
                                        batch_no: '-'
                                    }
                                }
                                : item
                        )
                    );
                    
                    // Clear form fields
                    setSelectedProduct("");
                    setProductQuantity("");
                    setProductNotes("");
                    
                    return { success: true };
                } else {
                    throw new Error(response.message || 'Failed to update product quantity');
                }
            } else {
                // Product doesn't exist, add new item
                const response = await addProductToPallet(
                    pallet.id,
                    selectedProduct,
                    parseInt(productQuantity),
                    productNotes.trim() || null
                );

                if (response.success || response.status === 200 || response.status === 201) {
                    // Find the product details from availableProducts (fetched in PalletEditor component)
                    const product = availableProducts.find(p => p.id === selectedProduct);
                    
                    // Create new item for local state
                    const newItem = {
                        id: response.data?.item?.id || Date.now(), // Use real ID if available, otherwise temp
                        pallet_id: pallet.id,
                        product_id: selectedProduct,
                        quantity: parseInt(productQuantity),
                        notes: productNotes.trim() || null,
                        product: product || { 
                            id: selectedProduct, 
                            product_code: 'Unknown', 
                            product_name: 'Product not found',
                            batch_no: '-'
                        } // Fallback if product not found in availableProducts
                    };
                    
                    setPalletItems(prevItems => [...prevItems, newItem]);
                    
                    // Clear form fields
                    setSelectedProduct("");
                    setProductQuantity("");
                    setProductNotes("");
                    
                    return { success: true };
                } else {
                    throw new Error(response.message || 'Failed to add product');
                }
            }
        } catch (error) {
            logger.error('Error adding product to pallet:', error);
            this.showAlert({
                message: `Failed to add product: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
            return { success: false };
        }
    }

    async updateItemQuantity(pallet, itemId, newQuantity, notes, setPalletItems, setEditedQuantities, setEditedNotes, setEditingItemId) {
        try {
            // Check if itemId is a composite ID (palletId_productId) and get real pallet_items.id
            let realItemId = itemId;
            if (typeof itemId === 'string' && itemId.includes('_')) {
                const parts = itemId.split('_');
                if (parts.length === 2) {
                    const productId = parts[1]; // Second part is the product ID
                    logger.log('🔍 Update: Extracted product ID from composite item ID:', productId);
                    
                    // Fetch the real pallet_items.id via API
                    try {
                        const { getSessionUUID } = await import('../../components/lib/AuthToken');
                        const sessionToken = await getSessionUUID();
                        
                        const response = await fetch(`/enhanced_warehouse/pallet-items/get-id`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${sessionToken}`
                            },
                            body: JSON.stringify({
                                pallet_id: pallet.id,
                                product_id: productId
                            })
                        });
                        
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: Failed to get item ID`);
                        }
                        
                        const data = await response.json();
                        if (!data.success || !data.item_id) {
                            throw new Error(data.message || 'Could not find pallet item');
                        }
                        
                        realItemId = data.item_id;
                        logger.log('✅ Update: Found real pallet_items.id:', realItemId);
                        
                    } catch (error) {
                        logger.error('❌ Update: Error fetching real item ID:', error);
                        throw new Error(`Failed to get item ID for update: ${error.message}`);
                    }
                }
            }

            const response = await updatePalletItemQuantity(pallet.id, realItemId, newQuantity, notes);
            
            if (response.success || response.status === 200) {
                // Update local state immediately (use original itemId for UI matching)
                setPalletItems(prevItems => 
                    prevItems.map(item => 
                        item.id === itemId 
                            ? { ...item, quantity: newQuantity, notes: notes }
                            : item
                    )
                );
                
                // Clear edited quantity and notes state
                setEditedQuantities(prev => {
                    const updated = { ...prev };
                    delete updated[itemId];
                    return updated;
                });
                
                setEditedNotes(prev => {
                    const updated = { ...prev };
                    delete updated[itemId];
                    return updated;
                });
                
                setEditingItemId(null);
                
                return { success: true };
            } else {
                throw new Error(response.message || 'Failed to update item quantity');
            }
        } catch (error) {
            logger.error('Error updating item quantity:', error);
            this.showAlert({
                message: `Failed to update quantity: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
            return { success: false };
        }
    }

    async removeItem(pallet, itemId, setPalletItems, productId) {
        return new Promise((resolve) => {
            this.showAlert({
                message: 'Are you sure you want to remove this item from the pallet?',
                type: "warning",
                buttons: [{
                    label: 'Yes',
                    onClick: async () => {
                        try {
                            const response = await removePalletItem(pallet.id, itemId, productId);
                            
                            if (response.success || response.status === 200) {
                                logger.log('🗑️ Item removed successfully, updating local state:', itemId);
                                // Update local state immediately
                                setPalletItems(prevItems => {
                                    const newItems = prevItems.filter(item => item.id !== itemId);
                                    logger.log('🗑️ Items before filter:', prevItems.length, 'after filter:', newItems.length);
                                    return newItems;
                                });
                                
                                resolve({ success: true });
                            } else if (response.status === 404) {
                                logger.log('🗑️ Item not found (404) - treating as already removed:', itemId);
                                // Item doesn't exist, but that's what we wanted - remove from UI
                                setPalletItems(prevItems => {
                                    const newItems = prevItems.filter(item => item.id !== itemId);
                                    logger.log('🗑️ Removed non-existent item from UI:', itemId);
                                    return newItems;
                                });
                                
                                this.showAlert({
                                    message: 'Item was already removed or does not exist',
                                    type: "info",
                                    buttons: [{ label: "Close", type: "primary" }],
                                });
                                
                                resolve({ success: true });
                            } else {
                                throw new Error(response.message || `Failed to remove item (HTTP ${response.status})`);
                            }
                        } catch (error) {
                            logger.error('❌ Error removing item:', error);
                            this.showAlert({
                                message: `Failed to remove item: ${error.message}`,
                                type: "danger",
                                buttons: [{ label: "Close", type: "primary" }],
                            });
                            resolve({ success: false });
                        }
                    }
                }, {
                    label: 'Cancel',
                    onClick: () => {
                        resolve({ success: false, cancelled: true });
                    }
                }]
            });
        });
    }

    async moveItem(sourcePalletId, itemId, destinationPalletId, setPalletItems, onRefreshData) {
        try {            
            // Check if itemId is a composite ID (palletId_productId) and get real pallet_items.id
            let realItemId = itemId;
            let productId = null;
            if (typeof itemId === 'string' && itemId.includes('_')) {
                const parts = itemId.split('_');
                if (parts.length === 2) {
                    productId = parts[1]; // Second part is the product ID
                    logger.log('🔍 Extracted product ID from composite item ID:', productId);
                    
                    // Fetch the real pallet_items.id via API
                    try {
                        const { getSessionUUID } = await import('../../components/lib/AuthToken');
                        const sessionToken = await getSessionUUID();
                        
                        const response = await fetch(`/enhanced_warehouse/pallet-items/get-id`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${sessionToken}`
                            },
                            body: JSON.stringify({
                                pallet_id: sourcePalletId,
                                product_id: productId
                            })
                        });
                        
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: Failed to get item ID`);
                        }
                        
                        const data = await response.json();
                        if (!data.success || !data.item_id) {
                            throw new Error(data.message || 'Could not find pallet item');
                        }
                        
                        realItemId = data.item_id;
                        logger.log('✅ Found real pallet_items.id:', realItemId);
                        
                    } catch (error) {
                        logger.error('❌ Error fetching real item ID:', error);
                        throw new Error(`Failed to get item ID: ${error.message}`);
                    }
                }
            }
            
            // Use the existing API with the real pallet_items.id
            const response = await movePalletItem(sourcePalletId, realItemId, destinationPalletId, productId);
            
            logger.log('🚚 Move response:', response);
            
            if (response.success || response.status === 200) {
                this.showAlert({
                    message: response.message || 'Product moved successfully',
                    type: "success",
                    buttons: [{ label: "Close", type: "primary" }],
                });
                
                // Pass move data to parent for local state updates
                if (onRefreshData) {
                    await onRefreshData({
                        type: 'move',
                        sourcePalletId: sourcePalletId,
                        destinationPalletId: destinationPalletId,
                        movedItem: { id: realItemId } // Pass the moved item info
                    });
                }
                
                return { success: true };
            } else {
                throw new Error(response.message || 'Failed to move item');
            }
        } catch (error) {
            logger.error('❌ Error moving item:', error);
            this.showAlert({
                message: `Failed to move product: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
            return { success: false };
        }
    }

    async getItemId(palletId, productId) {
        try {
            const { getSessionUUID } = await import('../../components/lib/AuthToken');
            const sessionToken = await getSessionUUID();
            
            const response = await fetch(`/enhanced_warehouse/pallet-items/get-id`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    pallet_id: palletId,
                    product_id: productId
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to get item ID`);
            }
            
            const data = await response.json();
            if (!data.success || !data.item_id) {
                throw new Error(data.message || 'Could not find pallet item');
            }
            
            return data.item_id;
        } catch (error) {
            logger.error('❌ Error fetching item ID:', error);
            this.showAlert({
                message: `Failed to get item ID: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
            return null;
        }
    }
        
}
